import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '../../../campaigns/hooks/useToast'
import { testSendSettings, testReceiveSettings } from '../services/B2BServices'
import { EMAIL_ACCOUNT_TYPES } from '../utils/campaignConstants'

export const useEmailSettingsTests = (accountType, userEditState, watchedValues) => {
  const { t } = useTranslation()
  const { showSuccess, showError } = useToast()
  const [testingSender, setTestingSender] = useState(false)
  const [testingReceiver, setTestingReceiver] = useState(false)

  const isTestSenderDisabled = useMemo(() => {
    if (testingSender || !watchedValues?.emailUserName?.trim()) {
      return true
    }
    if (accountType === EMAIL_ACCOUNT_TYPES.USERNAME_PASSWORD) {
      return !watchedValues?.emailPassword?.trim() ||
        !watchedValues?.smtpHostName?.trim() ||
        !watchedValues?.smtpPort?.toString()?.trim() ||
        !watchedValues?.transportStrategy?.trim()
    }
    if (accountType === EMAIL_ACCOUNT_TYPES.EXTERNAL_SERVICE) {
      return !watchedValues?.mailFromDomain?.trim()
    }
    return false
  }, [
    accountType,
    testingSender,
    watchedValues?.emailUserName,
    watchedValues?.emailPassword,
    watchedValues?.smtpHostName,
    watchedValues?.smtpPort,
    watchedValues?.transportStrategy,
    watchedValues?.mailFromDomain
  ])

  const isTestReceiverDisabled = useMemo(() => {
    if (testingReceiver || !watchedValues?.emailUserName?.trim()) {
      return true
    }
    if (accountType === EMAIL_ACCOUNT_TYPES.GMAIL_OAUTH2) {
      return !watchedValues?.inboxFolderName?.trim()
    }
    return !watchedValues?.emailPassword?.trim() ||
      !watchedValues?.rxHostName?.trim() ||
      !watchedValues?.rxPort?.toString()?.trim() ||
      !watchedValues?.rxProtocol?.trim() ||
      !watchedValues?.inboxFolderName?.trim()
  }, [
    accountType,
    testingReceiver,
    watchedValues?.emailUserName,
    watchedValues?.emailPassword,
    watchedValues?.rxHostName,
    watchedValues?.rxPort,
    watchedValues?.rxProtocol,
    watchedValues?.inboxFolderName
  ])

  const buildEmailSettings = useCallback((val) => {
    const toNull = (v) => v?.trim() ? v.trim() : null
    const toNumber = (v) => {
      const n = Number(v)
      return isNaN(n) ? null : n
    }

    const base = {
      accountType,
      emailUserName: toNull(val.emailUserName),
      inboxFolderName: toNull(val.inboxFolderName)
    }

    if (accountType === EMAIL_ACCOUNT_TYPES.USERNAME_PASSWORD) {
      return {
        ...base,
        emailPassword: toNull(val.emailPassword),
        smtpHostName: toNull(val.smtpHostName),
        smtpPort: toNumber(val.smtpPort),
        transportStrategy: toNull(val.transportStrategy),
        rxHostName: toNull(val.rxHostName),
        rxPort: toNumber(val.rxPort),
        rxProtocol: toNull(val.rxProtocol)
      }
    }

    if (accountType === EMAIL_ACCOUNT_TYPES.EXTERNAL_SERVICE) {
      const domain = val.emailUserName?.split('@')[1] || null
      return {
        ...base,
        emailPassword: toNull(val.emailPassword),
        domain,
        mailFromDomain: val.mailFromDomain?.trim()
          ? `${val.mailFromDomain.trim()}.${domain || ''}`
          : null,
        rxHostName: toNull(val.rxHostName),
        rxPort: toNumber(val.rxPort),
        rxProtocol: toNull(val.rxProtocol)
      }
    }

    // GMAIL_OAUTH2
    return {
      ...base,
      accessToken: userEditState?.emailSettings?.accessToken || null,
      refreshToken: userEditState?.emailSettings?.refreshToken || null,
      expireTimeInSeconds: userEditState?.emailSettings?.expireTimeInSeconds || null
    }
  }, [accountType, userEditState?.emailSettings])

  const testSenderSettings = useCallback((val) => {
    const emailSettings = buildEmailSettings(val)
    setTestingSender(true)
    testSendSettings(emailSettings).then(() => {
      showSuccess(t('mailTemplate.users.modal.senderTestSuccess'))
    }).catch(() => {
      showError(t('mailTemplate.users.modal.senderTestFailed'))
    }).finally(() => {
      setTestingSender(false)
    })
  }, [buildEmailSettings, showSuccess, showError, t])

  const testReceiverSettings = useCallback((val) => {
    const emailSettings = buildEmailSettings(val)
    setTestingReceiver(true)
    testReceiveSettings(emailSettings).then(() => {
      showSuccess(t('mailTemplate.users.modal.receiverTestSuccess'))
    }).catch(() => {
      showError(t('mailTemplate.users.modal.receiverTestFailed'))
    }).finally(() => {
      setTestingReceiver(false)
    })
  }, [buildEmailSettings, showSuccess, showError, t])

  return {
    testingSender,
    testingReceiver,
    isTestSenderDisabled,
    isTestReceiverDisabled,
    testSenderSettings,
    testReceiverSettings
  }
}
